import { Button, Badge } from "@jhool-io/fe-components";
import * as Sentry from "@sentry/react";
import {
    useFetchUserDetails,
    useFetchUserPractices,
} from "../../modules/auth/hooks/auth.queries";
import { IPractice } from "../../modules/auth/types/auth.types";
import axiosInstance from "../../utils/axios";
import { cn, removeEnumUnderscore } from "../../utils/helpers";
import { useAppDispatch } from "../../hooks/useRedux";
import { handleChangePractice } from "../../redux/features/practice/practiceSlice";
import BackLink from "../BackLink/BackLink";
import {
    getClientSession,
    setClientSession,
} from "../../utils/clientSessionStore";

interface PracticeSelectionProps {
    showBackLink?: boolean;
}

export default function PracticeSelection({
    showBackLink = true,
}: PracticeSelectionProps) {
    const { data: userDetails } = useFetchUserDetails();
    const { userId: storedUserId } = getClientSession();
    const userId = storedUserId || userDetails?.data?.user_id || "";
    const { data: clientPractices, isLoading: isLoadingPractices } =
        useFetchUserPractices(userId);
    const dispatch = useAppDispatch();

    const hasSubProfiles =
        userDetails?.data?.sub_profiles &&
        userDetails.data.sub_profiles.length > 0;

    const handleBackToSwitchProfile = () => {
        setClientSession({ userId: null });
        window.location.href = "/";
    };

    const handleSelectPractice = (practice: IPractice) => {
        axiosInstance.defaults.headers.common.practice_id =
            practice.practice_id;

        axiosInstance.defaults.headers.common.client_id = practice.client_id;

        setClientSession({
            clientId: practice.client_id,
            userId,
            practice,
        });

        dispatch(
            handleChangePractice({
                practice,
            })
        );
        Sentry.logger.info("Practice selected", {
            practice_id: practice.practice_id,
            practice_name: practice.practice_name,
            client_id: practice.client_id,
            user_id: userId,
        });
        window.location.href = "/";
    };

    const practices = clientPractices?.data?.practices ?? [];

    return (
        <div className="mx-auto overflow-y-auto px-16 py-24 sm:px-0 sm:overflow-y-auto sm:flex items-center justify-center min-h-dvh sm:min-h-auto">
            {isLoadingPractices && (
                <div className="page-loader">
                    <span />
                </div>
            )}
            {practices.length > 0 && (
                <div>
                    {showBackLink && hasSubProfiles && (
                        <div className="mb-16">
                            <BackLink onClick={handleBackToSwitchProfile} />
                        </div>
                    )}
                    <p className="text-xl mb-16 font-medium text-primary">
                        Select a practice to continue
                    </p>
                    <div className="flex flex-col gap-y-16">
                        {practices.map((practice) => {
                            const activeSessions =
                                practice.active_sessions ?? [];

                            const upcomingSessions =
                                practice.upcoming_sessions ?? [];

                            return (
                                <div
                                    key={practice.practice_id}
                                    className="rounded-r12 bg-white border border-[#E0E3EB] py-16 w-full sm:w-[600px]"
                                >
                                    <div className="flex items-start justify-between gap-12 px-16">
                                        <div className="flex items-center gap-8 min-w-0">
                                            <div className="w-40 h-40 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-base shrink-0">
                                                {practice.practice_name
                                                    ?.charAt(0)
                                                    .toUpperCase()}
                                            </div>
                                            <p className="text-base font-semibold break-words min-w-0">
                                                {practice.practice_name}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-12 shrink-0">
                                            <Button
                                                size="small"
                                                variant="primary"
                                                onClick={() =>
                                                    handleSelectPractice(
                                                        practice
                                                    )
                                                }
                                            >
                                                Open
                                            </Button>
                                        </div>
                                    </div>

                                    {activeSessions.length > 0 ? (
                                        <div className="mt-16 border-t border-[#E0E3EB] pt-12 px-24">
                                            <div className="flex flex-col gap-y-12">
                                                {activeSessions.map(
                                                    (session, index) => (
                                                        <div
                                                            key={
                                                                session.session_id
                                                            }
                                                            className={cn(
                                                                "flex items-center justify-between gap-16",
                                                                {
                                                                    "border-b border-[#E0E3EB]":
                                                                        activeSessions.length -
                                                                            1 !==
                                                                        index,
                                                                }
                                                            )}
                                                        >
                                                            <div className="flex flex-col">
                                                                <span className="flex flex-wrap items-center gap-4 text-sm font-medium capitalize">
                                                                    {removeEnumUnderscore(
                                                                        session.service
                                                                    )}
                                                                    <span className="text-gray">
                                                                        •
                                                                    </span>{" "}
                                                                    {removeEnumUnderscore(
                                                                        session.recurrence_rule
                                                                    )}
                                                                    <div>
                                                                        <Badge
                                                                            content={removeEnumUnderscore(
                                                                                session.delivery_option
                                                                            )}
                                                                            textColor="#212121"
                                                                            bgColor="#ebebeb"
                                                                            className="capitalize"
                                                                        />
                                                                    </div>
                                                                </span>
                                                                <span className="text-xs text-gray">
                                                                    with{" "}
                                                                    <span className="capitalize">
                                                                        {`${session.provider.first_name} ${session.provider.last_name}`}
                                                                    </span>
                                                                </span>
                                                            </div>
                                                            {upcomingSessions.some(
                                                                (
                                                                    upcomingSession
                                                                ) =>
                                                                    session.session_id ===
                                                                    upcomingSession.session_id
                                                            ) ? (
                                                                <div>
                                                                    <Badge
                                                                        content="Upcoming"
                                                                        textColor="#165574"
                                                                        bgColor="#acdefa7f"
                                                                    />
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
